"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  BALANCED_EQUATION_STRINGS,
  BalancedEquationCard,
  EquationField,
} from "@/components/tools/chem-reactions/balanced-equation";
import {
  GAS_CONDITIONS_STRINGS,
  GasConditionsFields,
} from "@/components/tools/chem-reactions/gas-conditions";
import {
  type AmountState,
  patchAmount,
  REACTANT_AMOUNT_STRINGS,
  ReactantAmountFields,
  readAmounts,
} from "@/components/tools/chem-reactions/reactant-amounts";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  AMOUNT_UNITS,
  amountFromMoles,
  DEFAULT_GAS_CONDITIONS,
  findAmountUnit,
  type GasConditionsInput,
  resolveMolarVolume,
} from "@/lib/tool-kit/chem-reactions/amounts";
import {
  type BalancedEquation,
  solveBalanced,
} from "@/lib/tool-kit/chem-reactions/balance";
import { limitingReactant } from "@/lib/tool-kit/chem-reactions/limiting";
import { speciesMass } from "@/lib/tool-kit/chem-reactions/stoichiometry";
import {
  percentYield,
  type YieldResult,
} from "@/lib/tool-kit/chem-reactions/yield";
import {
  fewestSigFigs,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENT_YIELD_CALCULATOR_STRINGS = {
  ...BALANCED_EQUATION_STRINGS,
  ...GAS_CONDITIONS_STRINGS,
  ...REACTANT_AMOUNT_STRINGS,
  equationPlaceholder: "N2(g) + H2(g) -> NH3(g)",
  modeLabel: "How do you know the theoretical yield?",
  modeKnown: "I already have it",
  modeReactants: "Work it out from the reactants",
  theoreticalLabel: "Theoretical yield",
  theoreticalHint: "The most product the limiting reactant could make.",
  actualLabel: "Actual yield",
  actualHint: "What you measured, in the same unit.",
  unitLabel: "Unit of both yields",
  actualUnitLabel: "Unit of the actual yield",
  productLabel: "Product you collected",
  results: "Results",
  percentYield: "Percent yield",
  theoreticalShown: "Theoretical yield",
  actualShown: "Actual yield",
  shortfall: "Not recovered",
  excess: "Above theoretical",
  limitingShown: "Limiting reactant",
  enterBoth:
    "Enter the theoretical and the actual yield to see the percent yield.",
  enterActual:
    "Enter the actual yield of the product to see the percent yield.",
  badTheoretical: "The theoretical yield must be a number greater than zero.",
  badActual: "The actual yield must be a number of zero or more.",
  noReactants: "Enter an amount for at least one reactant.",
  badProduct:
    "That unit cannot be applied to this product. Choose moles, or a mass for a species with a formula.",
  workingHeading: "The working",
  formulaLine:
    "Percent yield = actual ÷ theoretical × 100 = {actual} {unit} ÷ {theoretical} {unit} × 100 = {percent} %",
  theoryLine:
    "Theoretical yield of {species}: {moles} mol, which is {amount} {unit}",
  limitingLine: "Limiting reactant: {species}",
  sigNote:
    "Rounded to {count} significant figures, the fewest in the values you entered.",
  overNote:
    "A yield above 100 % is not possible for a pure, dry product. Usual causes are product still wet with solvent or water, impurities or by-products weighed with it, a weighing or transfer error, or a limiting reactant that was misidentified.",
  fullNote:
    "A yield of about 100 % means essentially all of the limiting reactant was converted to product and recovered. That is rare outside very clean, simple reactions, so check that the product is pure and dry.",
  lossNote:
    "Yields below 100 % are normal. Typical reasons are a reaction that is reversible or incomplete, side reactions that use up reactant, and product lost on transfer, filtering, washing and purification.",
  copy: "Copy result",
  copied: "Copied!",
  copyText:
    "Percent yield: {percent} % ({actual} {unit} of {theoretical} {unit})",
} as const;

type Strings = typeof PERCENT_YIELD_CALCULATOR_STRINGS;
type Mode = "known" | "reactants";

const EXAMPLES = [
  "N2(g) + H2(g) -> NH3(g)",
  "Fe2O3 + CO -> Fe + CO2",
  "C7H6O3 + C4H6O3 -> C9H8O4 + C2H4O2",
  "Zn + HCl -> ZnCl2 + H2(g)",
] as const;
const UNIT_OPTIONS = AMOUNT_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const DEFAULT_SIG = 3;
const PERCENT_SIG_MIN = 2;
const GUARD = 1;

function YieldAnswer({
  result,
  actual,
  theoretical,
  unit,
  sig,
  strings,
  fmt,
  extraLines,
}: {
  result: YieldResult;
  actual: number;
  theoretical: number;
  unit: string;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
  extraLines: readonly string[];
}) {
  const symbol = findAmountUnit(unit).symbol;
  const percent = formatSig(
    fmt,
    result.percent,
    Math.max(sig, PERCENT_SIG_MIN)
  );
  const line = t(strings.formulaLine, {
    actual: formatSig(fmt, actual, sig + GUARD),
    theoretical: formatSig(fmt, theoretical, sig + GUARD),
    unit: symbol,
    percent,
  });
  let note: string = strings.lossNote;
  if (result.band === "over") {
    note = strings.overNote;
  } else if (result.band === "full") {
    note = strings.fullNote;
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.percentYield}
          value={`${percent} %`}
        />
        <StatCard
          label={strings.theoreticalShown}
          value={`${formatSig(fmt, theoretical, sig)} ${symbol}`}
        />
        <StatCard
          label={strings.actualShown}
          value={`${formatSig(fmt, actual, sig)} ${symbol}`}
        />
        <StatCard
          label={result.shortfall >= 0 ? strings.shortfall : strings.excess}
          value={`${formatSig(fmt, Math.abs(result.shortfall), sig)} ${symbol}`}
        />
      </StatGrid>
      <Notice>{note}</Notice>
      <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <div className={WORKING_BOX}>
          {[...extraLines, line].map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          percent,
          actual: formatSig(fmt, actual, sig),
          theoretical: formatSig(fmt, theoretical, sig),
          unit: symbol,
        })}
      />
    </>
  );
}

function KnownMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [theoretical, setTheoretical] = useState("56.3");
  const [actual, setActual] = useState("42.5");
  const [unit, setUnit] = useState("g");
  const theoryValue = parseMeasuredNumber(theoretical);
  const actualValue = parseMeasuredNumber(actual);
  let body: ReactNode = null;
  if (theoretical.trim() === "" || actual.trim() === "") {
    body = <Notice>{strings.enterBoth}</Notice>;
  } else if (theoryValue === null || !(theoryValue > 0)) {
    body = <ErrorNotice>{strings.badTheoretical}</ErrorNotice>;
  } else if (actualValue === null || actualValue < 0) {
    body = <ErrorNotice>{strings.badActual}</ErrorNotice>;
  } else {
    const result = percentYield(actualValue, theoryValue);
    body = result ? (
      <YieldAnswer
        actual={actualValue}
        extraLines={[]}
        fmt={fmt}
        result={result}
        sig={fewestSigFigs([theoretical, actual], DEFAULT_SIG)}
        strings={strings}
        theoretical={theoryValue}
        unit={unit}
      />
    ) : null;
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.theoreticalHint}
          id="py-theoretical"
          label={strings.theoreticalLabel}
          numeric
          onChange={setTheoretical}
          value={theoretical}
        />
        <TextField
          hint={strings.actualHint}
          id="py-actual"
          label={strings.actualLabel}
          numeric
          onChange={setActual}
          value={actual}
        />
        <SelectField
          id="py-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={UNIT_OPTIONS}
          value={unit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function ReactantsResult({
  solved,
  states,
  gas,
  productIndex,
  actual,
  unit,
  strings,
  fmt,
}: {
  solved: Extract<BalancedEquation, { kind: "ok" }>;
  states: readonly AmountState[];
  gas: GasConditionsInput;
  productIndex: number;
  actual: string;
  unit: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const species = solved.equation.species;
  const molarVolumeValue = resolveMolarVolume(gas);
  const read = readAmounts(species, states, molarVolumeValue, strings);
  if (!read.ok) {
    return <ErrorNotice>{read.message}</ErrorNotice>;
  }
  const result = limitingReactant(
    species,
    solved.coefficients,
    read.moles,
    molarVolumeValue
  );
  if (result === null) {
    return <Notice>{strings.noReactants}</Notice>;
  }
  if (actual.trim() === "") {
    return <Notice>{strings.enterActual}</Notice>;
  }
  const actualValue = parseMeasuredNumber(actual);
  if (actualValue === null || actualValue < 0) {
    return <ErrorNotice>{strings.badActual}</ErrorNotice>;
  }
  const product = result.products.find((row) => row.index === productIndex);
  if (!product) {
    return null;
  }
  const productMass = speciesMass(product.species).total;
  const theoretical = amountFromMoles(
    product.moles,
    unit,
    productMass,
    molarVolumeValue
  );
  const yieldResult =
    theoretical === null ? null : percentYield(actualValue, theoretical);
  if (theoretical === null || yieldResult === null) {
    return <ErrorNotice>{strings.badProduct}</ErrorNotice>;
  }
  const sig = fewestSigFigs([...read.raws, actual], DEFAULT_SIG);
  const limiting = result.reactants
    .filter((row) => row.limiting)
    .map((row) => row.species.label)
    .join(", ");
  return (
    <>
      <StatGrid>
        <StatCard label={strings.limitingShown} value={limiting} />
      </StatGrid>
      <YieldAnswer
        actual={actualValue}
        extraLines={[
          t(strings.limitingLine, { species: limiting }),
          t(strings.theoryLine, {
            species: product.species.label,
            moles: formatSig(fmt, product.moles, sig + GUARD),
            amount: formatSig(fmt, theoretical, sig + GUARD),
            unit: findAmountUnit(unit).symbol,
          }),
        ]}
        fmt={fmt}
        result={yieldResult}
        sig={sig}
        strings={strings}
        theoretical={theoretical}
        unit={unit}
      />
    </>
  );
}

function ReactantsMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [equation, setEquation] = useState<string>(EXAMPLES[0]);
  const [states, setStates] = useState<AmountState[]>([
    { value: "50.0", unit: "g" },
    { value: "10.0", unit: "g" },
  ]);
  const [gas, setGas] = useState<GasConditionsInput>(DEFAULT_GAS_CONDITIONS);
  const [product, setProduct] = useState("2");
  const [actual, setActual] = useState("42.5");
  const [actualUnit, setActualUnit] = useState("g");
  const solved = solveBalanced(equation);
  const species = solved.kind === "ok" ? solved.equation.species : [];
  const products = species
    .map((item, index) => ({ item, index }))
    .filter((entry) => entry.item.side === "product");
  const productIndex = products.some((p) => String(p.index) === product)
    ? Number(product)
    : (products[0]?.index ?? 0);
  const needsGas =
    species.some((item) => item.state === "g") ||
    findAmountUnit(actualUnit).kind === "volume" ||
    states.some(
      (state, index) =>
        species[index]?.side === "reactant" &&
        findAmountUnit(state.unit).kind === "volume"
    );
  return (
    <>
      <EquationField
        examples={EXAMPLES}
        id="py-equation"
        onChange={setEquation}
        strings={strings}
        value={equation}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <BalancedEquationCard solved={solved} strings={strings} />
        {solved.kind === "ok" ? (
          <>
            <ReactantAmountFields
              idPrefix="py"
              onChange={(index, patch) =>
                setStates(patchAmount(states, index, patch))
              }
              species={species}
              states={states}
              strings={strings}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="py-product"
                label={strings.productLabel}
                onChange={setProduct}
                options={products.map((p) => ({
                  value: String(p.index),
                  label: p.item.label,
                }))}
                value={String(productIndex)}
              />
              <QuantityInput
                hint={strings.actualHint}
                id="py-actual-reactants"
                label={strings.actualLabel}
                onUnitChange={setActualUnit}
                onValueChange={setActual}
                unit={actualUnit}
                unitLabel={strings.actualUnitLabel}
                units={UNIT_OPTIONS}
                value={actual}
              />
            </div>
            {needsGas ? (
              <GasConditionsFields
                fmt={fmt}
                id="py-gas"
                onChange={setGas}
                strings={strings}
                value={gas}
              />
            ) : null}
            <ReactantsResult
              actual={actual}
              fmt={fmt}
              gas={gas}
              productIndex={productIndex}
              solved={solved}
              states={states}
              strings={strings}
              unit={actualUnit}
            />
          </>
        ) : null}
      </section>
    </>
  );
}

export default function PercentYieldCalculatorWidget() {
  const strings = useToolUi(PERCENT_YIELD_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("known");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="py-mode"
        onChange={setMode}
        options={[
          { value: "known", label: strings.modeKnown },
          { value: "reactants", label: strings.modeReactants },
        ]}
        value={mode}
      />
      {mode === "known" ? (
        <KnownMode fmt={fmt} strings={strings} />
      ) : (
        <ReactantsMode fmt={fmt} strings={strings} />
      )}
    </>
  );
}
